/* eslint-disable max-lines */
import React, { useContext, useEffect, useMemo, useState } from 'react';

import { gql } from '@apollo/client';
import { useTranslation } from '@modernloop/i18n';
import { FCWithFragments, MultiStepDialog } from '@modernloop/shared/components';
import { getLocalTimezone } from '@modernloop/shared/datetime';
import { useFlag } from '@modernloop/shared/feature-flag';
import { v4 as uuid } from 'uuid';

import {
  CandidateAvailabilityOptionsInterviewPlanQuery,
  CandidateAvailabilityOptionsModal_JobStageSettingsV2Fragment,
  CreateTaskDialogPage_InterviewPlanFragment,
  StringOrgPref,
  TaskCreationSource,
  useCandidateAvailabilityOptionsInterviewPlanLazyQuery,
  useCandidateAvailabilityOptionsInterviewPlanQuery,
  useCandidateAvailabilityOptionsModalQuery,
} from 'src/generated/mloop-graphql';

import ShareDialog, { CreateType } from 'src/components/Dialogs/ShareDialog';

import { EmployeeFragment } from 'src/entities/EmployeePicker/EmployeeGraphQL';
import cloneInterviewPlan from 'src/entities/InterviewPlan/cloneInterviewPlan';
import getSaveInterviewPlanInput from 'src/entities/InterviewPlan/getSaveInterviewPlanInput';

import { useOrgPrefString } from 'src/hooks/api/org';

import { CandidateAvailabilityOptions } from 'src/store/slices/candidate-availability-options';

import { useCandidatePortalUrl } from 'src/urls/hooks/useCandidatePortalUrl';

import { OrgPrefName } from 'src/utils/api/org';

import { SECONDS_IN_A_DAY } from 'src/constants';

import { TaskShareModalContext } from '../ApplicationDetailsTaskView/ApplicationContent/TaskShareDialogHandler';
import CreateTaskDialogPage from '../ScheduleTask/CreateModals/shared/CreateTaskDialogPage';
import { TaskCreateInputWrapper, generateIdempotencyTaskId } from '../ScheduleTask/CreateModals/shared/types';
import useDefaultTaskAssignee from '../ScheduleTask/CreateModals/shared/useDefaultTaskAssignee';

import Options from './Options';
import DeleteCandidateAvailabilityConfirmationDialog from './Options/DeleteCandidateAvailabilityConfirmationDialog';
import ShareOptions from './ShareOptions';
import { getBasicAvailabilityOptionsConfigV2 } from './getBasicAvailabilityOptionsConfig';
import { CandidateAvailabilityOptionsModalPage } from './types';

export type Fragments = {
  jobStageSettingsV2: CandidateAvailabilityOptionsModal_JobStageSettingsV2Fragment | undefined;
};

export type Props = {
  applicationId: string;
  candidateId: string;
  jobStageId: string;
  jobId: string;
  interviewPlanId?: string;
  /**
   * If taskId is provided, the modal will skip the task create step
   * If taskId is not provided, the modal will show the task create step and allow task creation
   */
  taskId?: string;
  /**
   * Show create task step
   */
  showCreateTaskStep?: boolean;
  timezone: string;
  initialPage?: CandidateAvailabilityOptionsModalPage;
  disableEditOptions?: boolean;
  taskOptions?: CandidateAvailabilityOptions;
  onClose: () => void;
  onSaveSuccess?: () => void;
  onDelete?: () => void;
  onJobStageIdChange?: (jobStageId: string) => void;
};

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GQL_CANDIDATE_AVAILABILITY = gql`
  ${CreateTaskDialogPage.fragments.application}
  ${EmployeeFragment}
  query CandidateAvailabilityOptionsModal($applicationId: uuid!) {
    application(id: $applicationId) {
      id
      ...CreateTaskDialogPage_application
    }
    thisEmployee {
      id
      ...Employee
    }
  }
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const JobStageInterviewPlan = gql`
  ${CreateTaskDialogPage.fragments.interviewPlan}
  ${cloneInterviewPlan.fragments.interviewPlan}
  ${getSaveInterviewPlanInput.fragments.interviewPlan}
  ${Options.fragments.interviewPlan}

  query CandidateAvailabilityOptionsInterviewPlan($jobStageId: uuid!) {
    jobStage(id: $jobStageId) {
      id
      ...CreateTaskDialogPage_interviewPlan
      ...cloneInterviewPlan_interviewPlan
      ...getSaveInterviewPlanInput_interviewPlan
      ...CandidateAvailabilityOption_interviewPlan
    }
  }
`;

export const CandidateAvailabilityOptionsModal: FCWithFragments<Fragments, Props> = ({
  applicationId,
  candidateId,
  jobStageId,
  jobId,
  interviewPlanId,
  taskId,
  showCreateTaskStep,
  timezone,
  jobStageSettingsV2: availabilitySettingsV2,
  initialPage,
  disableEditOptions,
  taskOptions,
  onClose,
  onSaveSuccess,
  onDelete,
  onJobStageIdChange,
}): JSX.Element => {
  const { t } = useTranslation();
  const orgBreakImprovementsEnabled = useFlag('org_break_improvements');
  const collaborationLinkEnabled = useFlag('prd-155-codesignal-template-select');

  const shareContext = useContext(TaskShareModalContext);
  const [currentPage, setCurrentPage] = useState<CandidateAvailabilityOptionsModalPage>(
    initialPage || CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION
  );
  const [shareViaShareDialog, setShareViaShareDialog] = useState(false);
  const [options, setOptions] = useState<CandidateAvailabilityOptions | undefined>(taskOptions);

  const { data: modalData, loading: modalDataLoading } = useCandidateAvailabilityOptionsModalQuery({
    variables: { applicationId },
    skip: !applicationId,
  });
  const { assignee, loading: assigneeLoading } = useDefaultTaskAssignee({
    applicationId,
    prefName: OrgPrefName.STR_TASK_DEFAULT_ASSIGNEE_AVAILABILITY_REQUESTS,
  });

  const { candidatePortalUrl } = useCandidatePortalUrl(applicationId, { taskId: options?.taskId || taskId });

  // Generate stable task ID for idempotency - persists across retries
  const idempotencyTaskId = useMemo(() => generateIdempotencyTaskId(), []);

  const [input, setInput] = React.useState<TaskCreateInputWrapper>({
    applicationId,
    jobStageId,
    assigneeEmployeeId: assignee?.id,
    assignee,
    candidateTimezone: getLocalTimezone(),
    isUrgent: false,
    isDebriefRequired: false,
    creationSource: TaskCreationSource.CandidateAvailabilityRequest,
    customInterviewPlan: [],
    customJobStageId: null,
    taskId: idempotencyTaskId,
  });

  const [interviewPlan, setInterviewPlan] = useState<CreateTaskDialogPage_InterviewPlanFragment>({
    id: '',
    jobStageInterviewGroups: [],
  });

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    if (modalDataLoading || !modalData?.application) {
      return;
    }

    if (modalData?.application?.candidate?.timezone) {
      setInput((prev) => ({
        ...prev,
        candidateTimezone: modalData.application?.candidate?.timezone,
      }));
    }

    if (modalData?.application?.job?.jobSettings.defaultTaskQueue?.id) {
      setInput((prev) => ({ ...prev, taskQueueId: modalData.application?.job?.jobSettings?.defaultTaskQueue?.id }));
    }
  }, [modalData, setInput, modalDataLoading]);

  const onInterviewPlanFetchCompleted = (data: CandidateAvailabilityOptionsInterviewPlanQuery) => {
    if (!data.jobStage) {
      return;
    }

    const newCustomJobStageId = interviewPlanId || uuid();

    const interviewPlanClone = cloneInterviewPlan(
      { interviewPlan: data.jobStage },
      { customJobStageId: newCustomJobStageId }
    );

    setInterviewPlan(interviewPlanClone);

    const interviewPlanInput = getSaveInterviewPlanInput(
      { interviewPlan: interviewPlanClone },
      { finalJobStageId: newCustomJobStageId, collaborationLinkEnabled }
    );

    if (orgBreakImprovementsEnabled) {
      setInput((prev) => ({
        ...prev,
        customJobStageId: newCustomJobStageId,
        interviewPlan: {
          groups: interviewPlanInput.groups,
          schedulingWindow: interviewPlanInput.schedulingWindow
            ? { seconds: interviewPlanInput.schedulingWindow.seconds }
            : undefined,
          excludedEmployeeIds: interviewPlanInput.excludedEmployeeIds || [],
        },
      }));
    } else {
      setInput((prev) => ({
        ...prev,
        customJobStageId: newCustomJobStageId,
        customInterviewPlan: interviewPlanInput.groups,
      }));
    }
  };

  const [defaultCommentPrefValue, { loading: defaultTaskCommentLoading }] = useOrgPrefString(
    StringOrgPref.StrDefaultTaskComment
  );

  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect bridges form state with changing inputs from outside this component.
  useEffect(() => {
    if (defaultTaskCommentLoading) {
      return;
    }

    setInput((prev) => ({
      ...prev,
      note: defaultCommentPrefValue || '',
    }));
  }, [defaultCommentPrefValue, defaultTaskCommentLoading, setInput]);

  // After we load the assignee, we need to set the assignee in the input
  // eslint-disable-next-line modernloop/no-use-effect.cjs -- Effect synchronizes local state from external inputs; prefer derived state when possible.
  useEffect(() => {
    if (assigneeLoading) {
      return;
    }

    setInput((prev) => ({
      ...prev,
      assigneeEmployeeId: assignee?.id,
      assignee,
    }));
  }, [assigneeLoading, assignee, setInput]);

  const { loading: interviewPlanLoading } = useCandidateAvailabilityOptionsInterviewPlanQuery({
    variables: { jobStageId: interviewPlanId || input.jobStageId || jobStageId },
    onCompleted: onInterviewPlanFetchCompleted,
  });

  const [fetchInterviewPlan, { loading: interviewPlanLazyLoading }] =
    useCandidateAvailabilityOptionsInterviewPlanLazyQuery({
      variables: { jobStageId: interviewPlanId || input.jobStageId || jobStageId },
      onCompleted: onInterviewPlanFetchCompleted,
    });

  const availabilityUrl = candidatePortalUrl;

  const handleOptionsChange = (newOptions: CandidateAvailabilityOptions) => {
    setOptions(newOptions);
  };

  if (currentPage === CandidateAvailabilityOptionsModalPage.SAVE_SUCCESS) {
    return (
      <ShareDialog
        longUrl={availabilityUrl}
        content={t('hiring-dashboard-candidates:candidates.availabilityOptions.shareDialogContent')}
        onClose={onClose}
        onSendEmail={() => {
          setShareViaShareDialog(true);
          setCurrentPage(CandidateAvailabilityOptionsModalPage.SHARE_AVAILABILITY);
        }}
      />
    );
  }

  const onOptionsBack =
    showCreateTaskStep && !taskId
      ? () => {
          setCurrentPage(CandidateAvailabilityOptionsModalPage.CREATE_TASK);
        }
      : undefined;

  const jsx = (
    <>
      {currentPage === CandidateAvailabilityOptionsModalPage.CREATE_TASK &&
        !assigneeLoading &&
        modalData?.application &&
        showCreateTaskStep && (
          <CreateTaskDialogPage
            allowEmptyPlan
            title={t('hiring-dashboard-candidates:candidates.availabilityOptions.requestAvailability')}
            application={modalData.application}
            onClose={onClose}
            input={input}
            interviewPlan={interviewPlan}
            interviewPlanLoading={interviewPlanLoading || interviewPlanLazyLoading}
            source={TaskCreationSource.CandidateAvailabilityRequest}
            setInput={setInput}
            onContinue={(newInput) => {
              setInput(newInput);

              const availabilityConfigV2 = getBasicAvailabilityOptionsConfigV2(
                {
                  settings: availabilitySettingsV2?.candidateAvailabilitySettingsV2 ?? undefined,
                },
                {
                  groups: (orgBreakImprovementsEnabled ? input.interviewPlan?.groups : input.customInterviewPlan) || [],
                }
              );

              if (options) {
                const rollingDaysSettings =
                  availabilitySettingsV2?.candidateAvailabilitySettingsV2?.useRollingDays?.value ?? true;

                let changeOptions: CandidateAvailabilityOptions = {
                  ...options,
                  advanceNoticeMinutes:
                    (availabilitySettingsV2?.candidateAvailabilitySettingsV2?.advanceNotice?.duration?.seconds ?? 0) /
                    60,
                  candidateNote: availabilitySettingsV2?.candidateAvailabilitySettingsV2?.candidateNote.value ?? '',
                  numberOfDays: availabilityConfigV2.numberOfDays,
                  minutesPerDays: availabilityConfigV2.minutesPerDays,
                  minimumTimeBlockMinutes: availabilityConfigV2.minimumTimeBlockMinutes,
                };

                changeOptions = {
                  ...changeOptions,
                  useRollingDays: rollingDaysSettings,
                  timeframeNumberOfDays:
                    availabilitySettingsV2?.candidateAvailabilitySettingsV2?.timeframe?.duration?.seconds /
                    SECONDS_IN_A_DAY,
                  canScheduleOverAvailableKeywords:
                    availabilitySettingsV2?.candidateAvailabilitySettingsV2?.canScheduleOverAvailableKeywords?.value ??
                    false,
                  canScheduleOverRecruitingKeywords:
                    availabilitySettingsV2?.candidateAvailabilitySettingsV2?.canScheduleOverRecruitingKeywords?.value ??
                    false,
                  canScheduleOverFreeTime:
                    availabilitySettingsV2?.candidateAvailabilitySettingsV2?.canScheduleOverFreeTime?.value ?? false,
                  shouldRespectLoadLimit:
                    availabilitySettingsV2?.candidateAvailabilitySettingsV2?.shouldRespectLoadLimit?.value ?? true,
                  candidateNote: availabilitySettingsV2?.candidateAvailabilitySettingsV2?.candidateNote.value ?? '',
                  bookIfPerfectScheduleFound:
                    availabilitySettingsV2?.candidateAvailabilitySettingsV2?.bookIfPerfectScheduleFound?.value ?? false,
                };

                handleOptionsChange(changeOptions);
              }

              setCurrentPage(CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION);
            }}
            onJobStageIdChange={(newJobStageId) => {
              onJobStageIdChange?.(newJobStageId);
              fetchInterviewPlan({ variables: { jobStageId: newJobStageId } });
            }}
            onInterviewPlanChange={(newInterviewPlan) => {
              setInterviewPlan(newInterviewPlan);

              const interviewPlanInput = getSaveInterviewPlanInput(
                { interviewPlan: newInterviewPlan },
                { finalJobStageId: newInterviewPlan.id, collaborationLinkEnabled }
              );

              if (orgBreakImprovementsEnabled) {
                setInput((prev) => ({
                  ...prev,
                  customJobStageId: newInterviewPlan.id,
                  interviewPlan: {
                    groups: interviewPlanInput.groups,
                    schedulingWindow: interviewPlanInput.schedulingWindow
                      ? { seconds: interviewPlanInput.schedulingWindow.seconds }
                      : undefined,
                    excludedEmployeeIds: interviewPlanInput.excludedEmployeeIds || [],
                  },
                }));
              } else {
                setInput((prev) => ({
                  ...prev,
                  customJobStageId: newInterviewPlan.id,
                  customInterviewPlan: interviewPlanInput.groups,
                }));
              }
            }}
          />
        )}
      {currentPage === CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION && options && (
        <Options
          applicationId={applicationId}
          taskId={taskId}
          taskInput={input}
          timezone={timezone}
          options={options}
          interviewPlan={interviewPlan}
          onOptionsChanged={handleOptionsChange}
          onBack={onOptionsBack}
          onContinue={() => {
            setCurrentPage(CandidateAvailabilityOptionsModalPage.SHARE_AVAILABILITY);
          }}
          onClose={onClose}
        />
      )}
      {currentPage === CandidateAvailabilityOptionsModalPage.SHARE_AVAILABILITY &&
        options &&
        availabilitySettingsV2 && (
          <ShareOptions
            applicationId={applicationId}
            candidateId={candidateId}
            jobStageId={jobStageId}
            jobStageSettingsV2={availabilitySettingsV2}
            options={options}
            taskId={taskId}
            jobId={jobId}
            taskCreatorId={modalData?.thisEmployee?.id}
            taskInput={input}
            onOptionsChanged={handleOptionsChange}
            onSaveSuccess={onSaveSuccess}
            onEditOptions={
              disableEditOptions || shareViaShareDialog
                ? undefined
                : () => setCurrentPage(CandidateAvailabilityOptionsModalPage.BASIC_CONFIGURATION)
            }
            onClose={(emailSent: boolean, createdTaskId: string) => {
              if (emailSent === undefined || emailSent === true) {
                onClose();
              } else if (shareContext?.onTaskShare) {
                shareContext.onTaskShare(CreateType.CANDIDATE_AVAILABILITY, createdTaskId);
                onClose();
              } else {
                setCurrentPage(CandidateAvailabilityOptionsModalPage.SAVE_SUCCESS);
              }
            }}
            ccRecipients={availabilitySettingsV2?.candidateAvailabilitySettingsV2?.ccRecipients.notificationPreference}
            bccRecipients={
              availabilitySettingsV2?.candidateAvailabilitySettingsV2?.bccRecipients.notificationPreference
            }
          />
        )}
      {options?.id && currentPage === CandidateAvailabilityOptionsModalPage.DELETE_AVAILABILITY_CONFIRMATION && (
        <DeleteCandidateAvailabilityConfirmationDialog
          candidateAvailabilityOptionsId={options.id}
          onClose={onClose}
          onDelete={onDelete}
        />
      )}
    </>
  );

  return (
    <MultiStepDialog open onClose={onClose}>
      {jsx}
    </MultiStepDialog>
  );
};

CandidateAvailabilityOptionsModal.fragments = {
  jobStageSettingsV2: gql`
    ${getBasicAvailabilityOptionsConfigV2.fragments.settings}
    ${ShareOptions.fragments.jobStageSettingsV2}
    fragment CandidateAvailabilityOptionsModal_jobStageSettingsV2 on JobStageSettingsV2 {
      id
      ...CandidateAvailabilityEmailPreview_jobStageSettingsV2
      availabilityRequestEmailTemplateId {
        templateId
      }
      candidateAvailabilitySettingsV2 {
        ...getBasicAvailabilityOptionsConfigV2_settings
        numberOfDays {
          value
        }
        minutesPerDays {
          value
        }
        minimumTimeBlockMinutes {
          value
        }
        advanceNotice {
          duration {
            seconds
          }
        }
        candidateNote {
          value
        }
        ccRecipients {
          notificationPreference {
            employeeIds
            dynamicAudiences
            externalEmailAddresses
          }
        }
        bccRecipients {
          notificationPreference {
            employeeIds
            dynamicAudiences
            externalEmailAddresses
          }
        }
        shouldRespectLoadLimit {
          value
        }
        bookIfPerfectScheduleFound {
          value
        }
        canScheduleOverRecruitingKeywords {
          value
        }
        canScheduleOverAvailableKeywords {
          value
        }
        canScheduleOverFreeTime {
          value
        }
        useRollingDays {
          value
        }
        timeframe {
          duration {
            seconds
          }
        }
      }
    }
  `,
};
